import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PencilLine, Reply, ReplyAll, Forward, Archive, ArchiveRestore, Star, MailOpen, Trash2, ExternalLink, Paperclip, ChevronDown, Loader2, ArrowLeft, Tag, X } from 'lucide-react';
import { MAIL_API, mailAuth, fmtMailDate, fmtSize, attachmentUrl, addrLabel, JobChip } from './mailUtils';
import { HtmlMail } from './HtmlMail';
import { MailComposer, buildDraft } from './MailComposer';
import { QuickReply } from './QuickReply';
import { SaveToDrive } from './SaveToDrive';
import { LabelPicker } from './LabelPicker';
import { ThreadNav } from './ThreadNav';
import { AttachmentsBar } from './AttachmentsBar';

const Btn = ({ onClick, title, children, testid, danger, disabled }) => (
  <button onClick={onClick} title={title} disabled={disabled} data-testid={testid}
    className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${danger ? 'text-slate-500 hover:bg-red-50 hover:text-red-600' : 'text-slate-500 hover:bg-slate-900/5 hover:text-slate-900'}`}>{children}</button>
);

const ActionBtn = ({ onClick, disabled, testid, icon: Icon, children }) => (
  <button onClick={onClick} disabled={disabled} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-sky-300 hover:bg-sky-50 disabled:opacity-40 text-xs text-slate-800 font-semibold flex items-center gap-1.5 shadow-sm transition-colors" data-testid={testid}>
    <Icon className="w-3.5 h-3.5" />{children}
  </button>
);

const IconBtn = ({ onClick, disabled, title, testid, icon: Icon }) => (
  <button onClick={onClick} disabled={disabled} title={title} aria-label={title} data-testid={testid}
    className="p-1.5 rounded-md text-slate-500 hover:text-sky-700 hover:bg-sky-50 disabled:opacity-30 disabled:hover:bg-transparent transition-colors">
    <Icon className="w-4 h-4" />
  </button>
);

const isDraftMsg = (m) => (m?.labels || []).includes('DRAFT');

const Message = React.forwardRef(({ m, index, total, open, current, onToggle, onReply, onReplyAll, onForward, onEditDraft, canSend, chips }, ref) => (
  <div ref={ref} className={`rounded-xl border transition-colors scroll-mt-14 ${open ? 'border-slate-200/70 bg-white shadow-sm' : 'border-transparent hover:bg-white'} ${current ? 'ring-2 ring-sky-200' : ''}`} data-testid={`mail-message-${m.id}`}>
    <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }} className="group w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer">
      <div className="w-8 h-8 rounded-full bg-sky-100 border border-sky-200 text-sky-700 text-xs font-black flex items-center justify-center shrink-0">{addrLabel(m.from).slice(0, 1).toUpperCase()}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-900 truncate">{m.mine ? 'me' : addrLabel(m.from)}</span>
          <span className="text-[11px] text-slate-500 truncate hidden sm:inline">{m.from.email}</span>
          {index === total - 1 && total > 1 && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">newest</span>}
          {isDraftMsg(m) && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`mail-draft-badge-${m.id}`}>draft</span>}
          <span className={`ml-auto flex items-center gap-0.5 shrink-0 ${open ? '' : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`} onClick={e => e.stopPropagation()} data-testid={`mail-header-actions-${m.id}`}>
            <IconBtn onClick={() => onReply(m)} disabled={!canSend} title="Reply" testid={`mail-hdr-reply-${m.id}`} icon={Reply} />
            <IconBtn onClick={() => onReplyAll(m)} disabled={!canSend} title="Reply all" testid={`mail-hdr-reply-all-${m.id}`} icon={ReplyAll} />
            <IconBtn onClick={() => onForward(m)} disabled={!canSend} title="Forward" testid={`mail-hdr-forward-${m.id}`} icon={Forward} />
          </span>
          <span className="text-[11px] text-slate-500 shrink-0 tabular-nums">{index + 1}/{total} · {fmtMailDate(m.date, true)}</span>
        </div>
        {open ? <div className="text-[11px] text-slate-500 truncate">to {m.to.map(addrLabel).join(', ')}{m.cc?.length ? ` · cc ${m.cc.map(addrLabel).join(', ')}` : ''}</div>
          : <div className="text-xs text-slate-500 truncate">{m.snippet}</div>}
      </div>
      {!open && <ChevronDown className="w-4 h-4 text-slate-400 mt-1" />}
    </div>
    {open && (
      <div className="px-4 pb-4">
        <HtmlMail message={m} />
        {m.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`mail-attachments-${m.id}`}>
            {m.attachments.map(a => (
              <span key={a.id} className="inline-flex items-center bg-slate-50 border border-slate-200 rounded-lg overflow-visible max-w-full min-w-0">
                <a href={attachmentUrl(a)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs hover:bg-slate-100 rounded-l-lg px-2.5 py-1.5 text-slate-800" data-testid={`mail-attachment-${a.id.slice(0, 12)}`}>
                  <Paperclip className="w-3 h-3 text-sky-600 shrink-0" /><span className="break-all">{a.filename}</span> <span className="text-slate-500 shrink-0">{fmtSize(a.size)}</span>
                </a>
                <span className="border-l border-slate-200 pl-0.5"><SaveToDrive attachment={a} chips={chips} /></span>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {isDraftMsg(m) && <ActionBtn onClick={() => onEditDraft(m)} disabled={!canSend} testid={`mail-edit-draft-${m.id}`} icon={PencilLine}>Edit draft</ActionBtn>}
          <ActionBtn onClick={() => onReply(m)} disabled={!canSend} testid={`mail-reply-${m.id}`} icon={Reply}>Reply</ActionBtn>
          <ActionBtn onClick={() => onReplyAll(m)} disabled={!canSend} testid={`mail-reply-all-${m.id}`} icon={ReplyAll}>Reply all</ActionBtn>
          <ActionBtn onClick={() => onForward(m)} disabled={!canSend} testid={`mail-forward-${m.id}`} icon={Forward}>Forward</ActionBtn>
        </div>
      </div>
    )}
  </div>
));

export const MailThreadView = ({ threadId, me, onChanged, onBack, labels = [], onLabelCreated, shortcutRef }) => {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(null);
  const [openIds, setOpenIds] = useState(new Set());
  const [current, setCurrent] = useState(-1);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const refs = useRef({});
  const scrollRef = useRef(null);
  const quiet = useRef(0); // ignore scroll-spy until this timestamp after a programmatic jump

  const load = (scrollNewest) => axios.get(`${MAIL_API}/threads/${threadId}`, mailAuth()).then(r => {
    setT(r.data);
    const msgs = r.data.messages;
    setOpenIds(new Set(msgs.filter((m, i) => m.unread || i === msgs.length - 1).map(m => m.id)));
    if (scrollNewest) setTimeout(() => go(msgs.length - 1, msgs), 60);
    if (r.data.can_send) onChanged?.({ id: threadId, unread: false });
  }).catch(e => setErr(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not open this thread'));

  useEffect(() => { setT(null); setErr(null); setCurrent(-1); load(true); }, [threadId]);

  const go = (i, msgs) => {
    const list = msgs || t?.messages || [];
    if (i < 0 || i >= list.length) return;
    const m = list[i];
    quiet.current = Date.now() + 900;
    setCurrent(i);
    setOpenIds(s => new Set([...s, m.id]));
    setTimeout(() => refs.current[m.id]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 20);
  };

  // which message is on screen → "N of M"
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !t) return undefined;
    const onScroll = () => {
      if (Date.now() < quiet.current) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) { setCurrent(t.messages.length - 1); return; }
      const top = el.getBoundingClientRect().top + 56;
      let idx = -1;
      t.messages.forEach((m, i) => { const r = refs.current[m.id]?.getBoundingClientRect(); if (r && r.top <= top + 8) idx = i; });
      if (idx >= 0) setCurrent(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [t]);

  const act = async (what) => {
    setBusy(true);
    try {
      if (what === 'trash') {
        await axios.post(`${MAIL_API}/threads/${threadId}/trash`, {}, mailAuth());
        toast.success('Moved to trash'); onChanged?.({ id: threadId, removed: true }); onBack?.();
      } else {
        const body = { archive: { remove: ['INBOX'] }, unarchive: { add: ['INBOX'] }, star: { add: ['STARRED'] }, unstar: { remove: ['STARRED'] }, unread: { add: ['UNREAD'] } }[what];
        await axios.post(`${MAIL_API}/threads/${threadId}/modify`, body, mailAuth());
        if (what === 'archive') { toast.success('Archived'); onChanged?.({ id: threadId, in_inbox: false, removed: true }); onBack?.(); }
        else if (what === 'unread') { onChanged?.({ id: threadId, unread: true }); onBack?.(); }
        else { setT(x => ({ ...x, starred: what === 'star', in_inbox: what === 'unarchive' ? true : x.in_inbox })); onChanged?.({ id: threadId, starred: what === 'star' }); }
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? `${d.message} ${d.action || ''}` : d || 'Could not update');
    }
    setBusy(false);
  };

  const labelAct = async (l, move) => {
    try {
      await axios.post(`${MAIL_API}/threads/${threadId}/modify`, move ? { add: [l.id], remove: ['INBOX'] } : { add: [l.id] }, mailAuth());
      toast.success(move ? `Moved to “${l.leaf || l.name}”` : `Labelled “${l.leaf || l.name}”`);
      if (move) { onChanged?.({ id: threadId, in_inbox: false, removed: true }); onBack?.(); } else load(false);
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'object' ? d.message : d || 'Could not label'); }
  };
  const removeLabel = async (id) => {
    try { await axios.post(`${MAIL_API}/threads/${threadId}/modify`, { remove: [id] }, mailAuth()); load(false); }
    catch (e) { toast.error('Could not remove label'); }
  };

  const openDraft = async (msg) => {
    try {
      const { data: f } = await axios.get(`${MAIL_API}/drafts/for-message/${msg.id}`, mailAuth());
      const { data: d } = await axios.get(`${MAIL_API}/drafts/${f.draft_id}`, mailAuth());
      setDraft({ mode: d.mode || 'new', to: d.to, cc: d.cc, bcc: d.bcc, subject: d.subject, body_html: d.body_html, message_id: d.message_id, thread_id: d.thread_id, draft_id: d.draft_id });
    } catch (e) { const dd = e.response?.data?.detail; toast.error(typeof dd === 'object' ? dd.message : dd || 'Could not open that draft'); }
  };

  const userLabelIds = useMemo(() => (t ? [...new Set(t.messages.flatMap(m => m.labels || []))].filter(id => id.startsWith('Label_')) : []), [t]);

  // keyboard: n / p / End / r / a / f / e / # / s  (delegated from MailPage)
  useEffect(() => {
    if (!shortcutRef) return undefined;
    shortcutRef.current = (key, ev) => {
      if (!t) return false;
      const last = t.messages[t.messages.length - 1];
      const cur = current >= 0 ? current : t.messages.length - 1;
      const map = {
        n: () => go(cur + 1), p: () => go(cur - 1), End: () => go(t.messages.length - 1),
        r: () => t.can_send && setDraft(buildDraft('reply', last, me)), a: () => t.can_send && setDraft(buildDraft('reply', last, me, true)),
        f: () => t.can_send && setDraft(buildDraft('forward', last, me)), e: () => t.can_send && act(t.in_inbox ? 'archive' : 'unarchive'),
        '#': () => t.can_send && act('trash'), s: () => t.can_send && act(t.starred ? 'unstar' : 'star'), U: () => t.can_send && act('unread'),
      };
      if (map[key]) { ev.preventDefault(); map[key](); return true; }
      return false;
    };
    return () => { shortcutRef.current = null; };
  });

  if (err) return <div className="p-6 text-sm text-red-600" data-testid="mail-thread-error">{String(err)}</div>;
  if (!t) return <div className="p-6 flex items-center gap-2 text-slate-500 text-sm" data-testid="mail-thread-loading"><Loader2 className="w-4 h-4 animate-spin" /> Opening…</div>;

  const toggle = (id) => setOpenIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const canSend = t.can_send;
  const last = t.messages[t.messages.length - 1];
  const allOpen = t.messages.every(m => openIds.has(m.id));

  return (
    <div className="flex flex-col h-full" data-testid="mail-thread-view">
      <div className="px-4 py-3 border-b border-slate-200/60 flex items-center gap-1 flex-wrap bg-white/40">
        {onBack && <Btn onClick={onBack} title="Back (u)" testid="mail-thread-back"><ArrowLeft className="w-4 h-4" /></Btn>}
        <h2 className="text-base md:text-lg font-black text-slate-900 tracking-tight flex-1 min-w-[200px] truncate" data-testid="mail-thread-subject">{t.subject}</h2>
        {t.chips?.map(c => <JobChip key={c.ref} chip={c} size="sm" />)}
        <div className="flex items-center ml-auto">
          <LabelPicker labels={labels} size="icon" mode="label" chips={t.chips || []} current={userLabelIds} onCreated={onLabelCreated} onPick={(l) => labelAct(l, false)} disabled={busy || !canSend} testid="mail-thread-label" />
          <LabelPicker labels={labels} size="icon" mode="move" chips={t.chips || []} current={userLabelIds} onCreated={onLabelCreated} onPick={(l) => labelAct(l, true)} disabled={busy || !canSend} testid="mail-thread-move" />
          <Btn onClick={() => act(t.starred ? 'unstar' : 'star')} title={t.starred ? 'Unstar (s)' : 'Star (s)'} testid="mail-thread-star" disabled={busy || !canSend}><Star className={`w-4 h-4 ${t.starred ? 'text-amber-500 fill-amber-400' : ''}`} /></Btn>
          <Btn onClick={() => act(t.in_inbox ? 'archive' : 'unarchive')} title={t.in_inbox ? 'Archive (e)' : 'Move to inbox'} testid="mail-thread-archive" disabled={busy || !canSend}>{t.in_inbox ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}</Btn>
          <Btn onClick={() => act('unread')} title="Mark unread (U)" testid="mail-thread-unread" disabled={busy || !canSend}><MailOpen className="w-4 h-4" /></Btn>
          <Btn onClick={() => act('trash')} title="Trash (#)" testid="mail-thread-trash" danger disabled={busy || !canSend}><Trash2 className="w-4 h-4" /></Btn>
          <a href={t.link} target="_blank" rel="noreferrer" className="p-2 rounded-lg text-slate-500 hover:bg-slate-900/5 hover:text-slate-900" title="Open in Gmail" data-testid="mail-thread-gmail"><ExternalLink className="w-4 h-4" /></a>
        </div>
      </div>
      {(userLabelIds.length > 0) && (
        <div className="px-4 pt-2 flex items-center gap-1 flex-wrap" data-testid="mail-thread-labels">
          {userLabelIds.map(id => { const l = labels.find(x => x.id === id); return (
            <span key={id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700" data-testid={`mail-thread-label-${id}`}>
              <Tag className="w-3 h-3 text-slate-400" />{l ? l.name : id}
              {canSend && <button onClick={() => removeLabel(id)} className="text-slate-400 hover:text-red-600" title="Remove label"><X className="w-3 h-3" /></button>}
            </span>); })}
        </div>
      )}
      {!canSend && <p className="mx-4 mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mail-readonly-notice">Read-only for now — reply, archive and star switch on once the Workspace admin adds the gmail.modify scope.</p>}
      <AttachmentsBar threadId={threadId} messages={t.messages} chips={t.chips || []} />
      <div ref={scrollRef} className="relative px-4 pb-4 space-y-2 overflow-y-auto flex-1">
        <ThreadNav messages={t.messages} currentIndex={current} onGo={go} allOpen={allOpen}
          onExpandAll={() => setOpenIds(new Set(t.messages.map(m => m.id)))} onCollapseAll={() => setOpenIds(new Set([last.id]))} />
        <div className="space-y-2 pt-2">
          {t.messages.map((m, i) => (
            <Message key={m.id} ref={el => { refs.current[m.id] = el; }} m={m} index={i} total={t.messages.length} open={openIds.has(m.id)} current={current === i} onToggle={() => { toggle(m.id); setCurrent(i); }} canSend={canSend} chips={t.chips || []}
              onReply={msg => setDraft(buildDraft('reply', msg, me))} onReplyAll={msg => setDraft(buildDraft('reply', msg, me, true))} onForward={msg => setDraft(buildDraft('forward', msg, me))} onEditDraft={openDraft} />
          ))}
        </div>
        {canSend && last && isDraftMsg(last) && (
          <button type="button" onClick={() => openDraft(last)} className="w-full text-left px-4 py-3 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 text-sm text-amber-900 hover:bg-amber-50 hover:border-amber-400 transition-colors inline-flex items-center gap-2" data-testid="mail-thread-continue-draft"><PencilLine className="w-4 h-4" />Continue your draft…</button>
        )}
        {canSend && last && !isDraftMsg(last) && (
          <QuickReply last={last} me={me} chips={t.chips || []} canSend={canSend} onExpand={(d) => setDraft(d)} onSent={() => { load(true); onChanged?.({ id: threadId, refresh: true }); }} />
        )}
      </div>
      {draft && <MailComposer draft={{ ...draft, chips: t.chips || [] }} onClose={(hadDraft) => { setDraft(null); if (hadDraft) setTimeout(() => load(true), 800); }} onSent={() => { load(true); onChanged?.({ id: threadId, refresh: true }); }} onDiscarded={() => { setTimeout(() => { load(true); onChanged?.({ id: threadId, refresh: true }); }, 800); }} />}
    </div>
  );
};
